// Long/short exposure aggregation by symbol. Lots are summed exactly via the decimal helper;
// bar fractions are parsed to numbers for layout only.

import type { Position } from '../types/api';
import { sumDec, toNum } from './decimal';

export interface ExposureRow {
  sym: string;
  buyLots: string;
  sellLots: string;
  buyFrac: number;
  sellFrac: number;
}

export function exposureBySymbol(positions: Position[]): ExposureRow[] {
  const buys = new Map<string, string[]>();
  const sells = new Map<string, string[]>();
  for (const p of positions) {
    const bucket = p.side === 'buy' ? buys : sells;
    const arr = bucket.get(p.symbol) ?? [];
    arr.push(p.volume);
    bucket.set(p.symbol, arr);
  }
  const symbols = Array.from(new Set([...buys.keys(), ...sells.keys()])).sort();
  const rows: ExposureRow[] = symbols.map((sym) => ({
    sym,
    buyLots: sumDec(buys.get(sym) ?? []),
    sellLots: sumDec(sells.get(sym) ?? []),
    buyFrac: 0,
    sellFrac: 0,
  }));
  let max = 1;
  for (const r of rows) {
    max = Math.max(max, toNum(r.buyLots) ?? 0, toNum(r.sellLots) ?? 0);
  }
  for (const r of rows) {
    r.buyFrac = (toNum(r.buyLots) ?? 0) / max;
    r.sellFrac = (toNum(r.sellLots) ?? 0) / max;
  }
  return rows;
}

/** Sum floating P&L exactly, grouped by currency (never mix currencies). */
export function netFloatingByCurrency(items: Array<{ profit: string; swap: string; currency: string }>): Map<string, string> {
  const byCcy = new Map<string, string[]>();
  for (const it of items) {
    const arr = byCcy.get(it.currency) ?? [];
    arr.push(it.profit, it.swap);
    byCcy.set(it.currency, arr);
  }
  const out = new Map<string, string>();
  for (const [ccy, list] of byCcy) out.set(ccy, sumDec(list));
  return out;
}
